<!--
 *@Author: lx
 *@Date: 2026-01-28
 *@Description: 通用侧边栏组件 - 支持画廊分类和文章TOC两种模式
 * mode="gallery" - 画廊模式，移动端底部弹出
 * mode="toc" - 文章TOC模式，移动端右侧弹出
-->
<script setup lang="ts">
import { watch, onUnmounted, computed } from 'vue'

export interface SidebarItem {
  id: string
  text: string
  level?: number // TOC模式下的层级
  isMapSection?: boolean // 是否是地图章节
}

const props = withDefaults(defineProps<{
  mode: 'gallery' | 'toc'
  items: SidebarItem[]
  activeId?: string
  title?: string
  subtitle?: string
  isOpen?: boolean
}>(), {
  mode: 'gallery',
  activeId: '',
  title: '',
  subtitle: '',
  isOpen: false
})

const emit = defineEmits<{
  (e: 'select', item: SidebarItem): void
  (e: 'close'): void
}>()

// 监听打开状态，锁定 body 滚动
watch(() => props.isOpen, (newVal) => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = newVal ? 'hidden' : ''
  }
})

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})

const handleSelect = (item: SidebarItem) => {
  emit('select', item)
}

const handleClose = () => {
  emit('close')
}

// 根据模式计算样式类名
const sidebarClass = computed(() => ({
  'unified-sidebar': true,
  'mode-gallery': props.mode === 'gallery',
  'mode-toc': props.mode === 'toc',
  'is-open': props.isOpen
}))
</script>

<template>
  <!-- 遮罩层 -->
  <Teleport to="body">
    <Transition name="overlay-fade">
      <div 
        v-if="isOpen" 
        class="sidebar-overlay"
        :class="{ 'mode-toc': mode === 'toc' }"
        @click="handleClose"
      ></div>
    </Transition>
  </Teleport>

  <!-- 侧边栏 -->
  <aside :class="sidebarClass">
    <!-- 头部信息 -->
    <div class="sidebar-header">
      <div class="header-content">
        <h3 v-if="title" class="sidebar-title">{{ title }}</h3>
        <p v-if="subtitle" class="sidebar-subtitle">{{ subtitle }}</p>
      </div>
      <!-- TOC 模式显示关闭按钮在右上角 -->
      <button 
        v-if="mode === 'toc'" 
        class="close-btn-top" 
        @click="handleClose" 
        aria-label="关闭"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
    </div>

    <!-- 导航列表 -->
    <nav class="sidebar-nav">
      <ul>
        <li 
          v-for="item in items" 
          :key="item.id"
          :class="[
            { active: activeId === item.id },
            item.level ? `level-${item.level}` : ''
          ]"
          @click="handleSelect(item)"
        >
          <!-- 地图章节显示定位图标 -->
          <svg 
            v-if="item.isMapSection" 
            class="map-marker-icon" 
            xmlns="http://www.w3.org/2000/svg" 
            width="14" 
            height="14" 
            viewBox="0 0 24 24" 
            fill="currentColor"
          >
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span class="item-text">{{ item.text }}</span>
        </li>
      </ul>
    </nav>

    <!-- 底部关闭按钮（画廊模式移动端） -->
    <button 
      v-if="mode === 'gallery'"
      class="close-btn-bottom" 
      @click="handleClose" 
      aria-label="关闭菜单"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
    </button>
  </aside>
</template>

<style scoped>
/* ============ 通用样式 ============ */
.unified-sidebar {
  width: 220px;
  flex-shrink: 0;
  position: sticky;
  top: 5rem;
  height: fit-content;
  text-align: left;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content {
  flex: 1;
}

.sidebar-title {
  font-size: 1.25rem;
  margin: 0 0 0.5rem 0;
  font-weight: bold;
}

.sidebar-subtitle {
  font-size: 0.75rem;
  color: var(--muted-color, #999);
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.sidebar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-nav li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  color: var(--text-color, #333);
  padding: 0.5rem 0;
  border-right: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
  font-size: 0.95rem;
}

/* 地图定位图标样式 */
.map-marker-icon {
  flex-shrink: 0;
  color: #e74c3c;
  opacity: 0.8;
}

.sidebar-nav li:hover .map-marker-icon,
.sidebar-nav li.active .map-marker-icon {
  opacity: 1;
}

.sidebar-nav li:hover,
.sidebar-nav li.active {
  color: var(--text-color, #333);
  font-weight: bold;
  border-right-color: var(--text-color, #333);
}

.close-btn-top,
.close-btn-bottom {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color, #333);
  padding: 4px;
}

.close-btn-bottom {
  margin-top: 1rem;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border-color, #eee);
  border-radius: 4px;
  justify-content: center;
  align-items: center;
}

/* 遮罩层 */
.sidebar-overlay {
  display: none;
}

/* ============ TOC 模式特有样式 ============ */
.mode-toc .sidebar-subtitle {
  margin-bottom: 1rem;
}

/* TOC 层级缩进 */
.mode-toc .level-1 { font-weight: 600; }
.mode-toc .level-2 { padding-left: 1rem; }
.mode-toc .level-3 { padding-left: 2rem; font-size: 0.9rem; }
.mode-toc .level-4 { padding-left: 3rem; font-size: 0.85rem; }

/* Mobile/Tablet/Small Desktop Styles - Drawer Mode */
@media (max-width: 1536px) {
  /* 遮罩层显示 */
  .sidebar-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 998;
    transition: opacity 0.3s ease;
  }

  .sidebar-overlay.mode-toc {
    /* backdrop-filter: blur(2px); */
  }

  /* ========== 画廊模式：底部弹出 ========== */
  .mode-gallery {
    width: 100%;
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--bg-color, #fff);
    padding: 1rem;
    z-index: 999;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    border-top: 1px solid var(--border-color, #eee);
    border-radius: 16px 16px 0 0;
    display: flex;
    flex-direction: column;
    height: auto;
    max-height: 50vh;
    overflow-y: auto;
    box-sizing: border-box;
    margin-bottom: 0;
    border-left: none;
  }

  .mode-gallery.is-open {
    transform: translateY(0);
  }

  .mode-gallery .close-btn-bottom {
    display: flex;
    border: none;
    margin-top: 0.5rem;
  }

  .mode-gallery .sidebar-nav li {
    padding: 0.8rem 0;
    border-bottom: 1px solid var(--border-color, #eee);
    border-right: none;
  }

  .mode-gallery .sidebar-nav li:hover,
  .mode-gallery .sidebar-nav li.active {
    border-right: none;
    border-bottom-color: var(--text-color, #333);
  }

  /* ========== TOC 模式：右侧弹出 ========== */
  .unified-sidebar.mode-toc {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 280px;
    max-width: 80%;
    height: 100%;
    background-color: var(--bg-color, #fff);
    padding: 0;
    z-index: 999;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 0;
    border-left: 1px solid var(--border-color, #eee);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
  }

  .unified-sidebar.mode-toc.is-open {
    transform: translateX(0);
  }

  .unified-sidebar.mode-toc .sidebar-header {
    padding: 20px;
    border-bottom: 1px solid var(--border-color, #eee);
    flex-shrink: 0;
  }

  .unified-sidebar.mode-toc .sidebar-title {
    margin-bottom: 0;
  }

  .unified-sidebar.mode-toc .sidebar-subtitle {
    margin-bottom: 0;
    margin-top: 0.25rem;
  }

  .unified-sidebar.mode-toc .close-btn-top {
    display: block;
    flex-shrink: 0;
  }

  .unified-sidebar.mode-toc .sidebar-nav {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 0;
  }

  .unified-sidebar.mode-toc .sidebar-nav li {
    padding: 0.6rem 1.25rem;
    border-right: none;
    border-left: 3px solid transparent;
    margin-bottom: 0;
  }

  .unified-sidebar.mode-toc .sidebar-nav li:hover,
  .unified-sidebar.mode-toc .sidebar-nav li.active {
    border-right: none;
    border-left-color: var(--text-color, #333);
    background: var(--hover-bg, #f9f9f9);
  }

  /* TOC 层级缩进调整 */
  .unified-sidebar.mode-toc .level-1 { padding-left: 1.25rem; }
  .unified-sidebar.mode-toc .level-2 { padding-left: 2rem; }
  .unified-sidebar.mode-toc .level-3 { padding-left: 2.75rem; }
  .unified-sidebar.mode-toc .level-4 { padding-left: 3.5rem; }
}

/* ============ Transition 动画 ============ */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.overlay-fade-enter-to,
.overlay-fade-leave-from {
  opacity: 1;
}

/* ============ 深色模式 ============ */
[data-theme='dark'] .unified-sidebar,
[data-theme='dark'] .mode-gallery,
[data-theme='dark'] .mode-toc {
  /* background-color: rgba; 这里设置黑色会导致覆盖页面内容，看不清*/
  color: #f0f0f0;
}

[data-theme='dark'] .sidebar-nav li:hover,
[data-theme='dark'] .sidebar-nav li.active {
  color: #fff;
}

[data-theme='dark'] .mode-toc .sidebar-nav li:hover,
[data-theme='dark'] .mode-toc .sidebar-nav li.active {
  background: #2a2a2a;
}
</style>
